<template>
  <div
    :class="$style.container"
    v-if="participants && projectAuditDataCollectionRecords"
  >
    <div :class="$style.titleSection">
      <div :class="$style.title">{{ title }}</div>
      <button
        v-if="participants.length > maxItemDisplay"
        @click="handleViewAll"
        class="button"
        :class="$style.button"
      >
        <span>View all</span>
        <span class="icon">
          <i class="fas fa-chevron-down"></i>
        </span>
      </button>
    </div>

    <div
      :class="$style.grid"
      v-if="items.length > 0 && projectAuditDataCollectionRecords"
    >
      <AvatarCard
        v-for="(participant, index) in items"
        :key="`participant${index}`"
        :class="$style.item"
        :avatarText="participant.userEmail[0] || ''"
      >
        <p v-if="participant.participantName.length > 25">
          <b-tooltip
            :label="participant.participantName"
            position="is-top"
            size="is-small"
          >
            <b>
              {{ participant.participantName.substring(0, 25) + '...' }}
            </b>
          </b-tooltip>
        </p>
        <p v-else>
          <b>
            {{ participant.participantName }}
          </b>
        </p>
        <br />
        <span
          v-for="(lastSubmission, lastSubmissionIndex) in getLastSubmission(
            projectAuditDataCollectionRecords,
            participant.userEmail,
          )"
          :key="lastSubmissionIndex"
        >
          <span
            :class="$style.participantText"
            v-if="lastSubmissionIndex === 0"
          >
            Last submission: {{ lastSubmission }}<br />
          </span>
          <span :class="$style.participantText" v-else>
            {{ lastSubmission }}<br />
          </span>
        </span>
        <br />
        Total Data Collected<br />
        {{ totalDataCollection(dataCollectionRecords, participant.userEmail) }}
      </AvatarCard>
    </div>
    <div v-else>
      <p-data-table
        :data="items"
        :hoverable="true"
        :noDataText="`No participants found`"
      >
      </p-data-table>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import Card from '@/components/Card.vue';
import AvatarCard from '@/components/AvatarCard.vue';
import { RootState } from '@/store/store';
import {
  ProjectAuditDataCollectionRecordsPayload,
  ParticipantListDTO,
} from '@/store/modules/projects/types/projects.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import moment from 'moment';
import { getJBIUsername } from '@/jbi-shared/util/user.utils';

@Component({
  components: {
    Card,
    AvatarCard,
  },
})
export default class ParticipantList extends Vue {
  @Prop() public title!: string;
  @Prop({ default: 0 }) public maxItemDisplay!: number;
  @Prop() public participants!: ParticipantListDTO[];
  @Prop() public auditList!: any;
  @Prop() public projectId!: number;

  public viewAll = false;
  public auditIds: number[] = [];
  public dataCollectionRecords: any = [];

  @Action('projects/getProjectAuditDataCollectionRecords')
  public getProjectAuditDataCollectionRecords!: (
    payload: ProjectAuditDataCollectionRecordsPayload,
  ) => void;

  @State((state: RootState) => state.projects.projectAuditDataCollectionRecords)
  public projectAuditDataCollectionRecords!: ProjectAuditDataCollectionRecordsPayload;

  @Watch('projectAuditDataCollectionRecords')
  @isTruthy
  public watchProjectAuditDataCollectionRecords(newValue: any) {
    newValue.hits.map((data: any) => {
      data._source.dataCollectionRecords.map((value: any) => {
        this.dataCollectionRecords.push({
          email: data._source.user.email,
          total: value.total,
        });
      });
    });
  }

  public totalDataCollection(value: any, email: any) {
    let totalValue = 0;
    value.map((data: any) => {
      if (data.email === email) {
        totalValue += Number(data.total);
      }
    });
    return totalValue;
  }

  public getLastSubmission(activityRecords: any, email: string) {
    const userActivityRecords = activityRecords.hits.filter(
      (activityRecord: any) => {
        return activityRecord._source.user.email === email;
      },
    );
    switch (userActivityRecords.length) {
      case 0:
        return ['-', ''];
      case 1:
        return [
          this.getFormattedTimeGap(userActivityRecords[0]._source.updatedAt),
          userActivityRecords[0]._source.audit.name,
        ];
      default:
        const lastSubmission = userActivityRecords.reduce(
          (firstRecord: any, secondRecord: any) => {
            return moment(secondRecord._source.updatedAt) >
              moment(firstRecord._source.updatedAt)
              ? secondRecord
              : firstRecord;
          },
        );
        return [
          this.getFormattedTimeGap(lastSubmission._source.updatedAt),
          lastSubmission._source.audit.name,
        ];
    }
  }

  public getFormattedTimeGap(lastSubmissionDate: string) {
    const timeGap = moment(lastSubmissionDate).fromNow();
    if (timeGap === 'a day ago') {
      return 'Yesterday';
    } else if (timeGap.includes('days')) {
      return moment(lastSubmissionDate).format('DD MMM YYYY');
    } else {
      return timeGap.charAt(0).toUpperCase() + timeGap.slice(1);
    }
  }

  get items() {
    const participantData: Array<{
      userEmail: string;
      participantName: string;
    }> = [];
    this.participants.forEach((item: ParticipantListDTO) => {
      const participantName: string = item.participant
        ? getJBIUsername(
            item.participant.user.email,
            item.participant.user.oicPayload,
          )
        : item.userEmail;

      participantData.push({
        userEmail: item.userEmail,
        participantName,
      });
    });
    if (this.viewAll) {
      return participantData;
    }
    return participantData.length > 0
      ? participantData.slice(0, this.maxItemDisplay)
      : participantData;
  }

  public handleViewAll() {
    this.$emit('changeActiveTab', '#participants');
  }

  public async mounted() {
    this.auditList.map((data: any) => {
      this.auditIds.push(data.id);
    });
    const projectAuditId = [];
    projectAuditId.push({
      projectId: this.projectId,
      auditIds: this.auditIds,
    });
    if (this.auditIds.length > 0) {
      await this.getProjectAuditDataCollectionRecords(projectAuditId[0]);
    }
  }
}
</script>

<style lang="scss" module>
.container {
  margin-bottom: 5rem;

  .titleSection {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .title {
      font-weight: bold;
      text-transform: uppercase;
      padding: 8px 0;
    }

    .button {
      border: none;
      background: transparent;
      color: $primary;
    }
  }

  .grid {
    display: grid;
    grid-gap: 2rem;
    grid-template-columns: repeat(
      auto-fill,
      minmax(300px, 1fr)
    ); // auto-break & column min-width 300px and max-width 1fr;
    grid-auto-rows: minmax(150px, auto);
    grid-auto-flow: dense;

    .item {
      &:hover {
        background-color: #f7fbff;
        cursor: pointer;
      }
    }
    .participantText {
      font-weight: 400;
      font-size: 12px;
    }
  }
}
</style>
